import type { Network } from '@aptos-labs/ts-sdk';
import { Aptos, AptosConfig } from '@aptos-labs/ts-sdk';
import type {
  AccountInfo,
  AptosFeatures,
  AptosWallet,
} from '@aptos-labs/wallet-standard';
import { UserResponseStatus } from '@aptos-labs/wallet-standard';
import {
  type DynamicClient,
  UserRejectedError,
  assertWalletAccountSigningAvailability,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';

import type { AptosWalletProvider } from '../../../AptosWalletProvider.types';
import { aptosTransferAmount } from '../../../utils/aptosTransferAmount';

type CreateWalletProviderFromAptosStandardWalletParams = {
  dynamicClient: DynamicClient;
  walletStandardWallet: AptosWallet;
};

export const createWalletProviderFromAptosStandardWallet = ({
  dynamicClient,
  walletStandardWallet,
}: CreateWalletProviderFromAptosStandardWalletParams): AptosWalletProvider => {
  const features: AptosFeatures = walletStandardWallet.features;

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain: 'APTOS',
    displayName: walletStandardWallet.name,
    walletProviderType,
  });

  const getAutoConnectedAccount = ():
    | { address: string; publicKey: string }[]
    | undefined => {
    const accounts = walletStandardWallet.accounts;
    if (!accounts?.length || !accounts[0].publicKey || !accounts[0].address) {
      return;
    }

    return accounts.map((account) => ({
      address: account.address.toString(),
      publicKey: account.publicKey?.toString(),
    }));
  };

  const connect: AptosWalletProvider['connect'] = async () => {
    const autoConnectedAccounts = getAutoConnectedAccount();

    if (autoConnectedAccounts) {
      return {
        addresses: autoConnectedAccounts,
      };
    }

    const connectMethod = features['aptos:connect']?.connect;

    assertDefined(connectMethod, 'connect method not found in wallet features');

    const result = await connectMethod();

    if (result.status !== UserResponseStatus.APPROVED) {
      throw new UserRejectedError({ action: 'connect' });
    }

    return {
      addresses: [
        {
          address: result.args.address.toString(),
          publicKey: result.args.publicKey?.toString(),
        },
      ],
    };
  };

  const disconnect: AptosWalletProvider['disconnect'] = async () => {
    const disconnectMethod = features['aptos:disconnect']?.disconnect;

    // this should never happen as we check for all required features in hasAllRequiredFeatures
    // but we'll assert it just in case
    assertDefined(
      disconnectMethod,
      'disconnect method not found in wallet features'
    );

    return disconnectMethod();
  };

  const getActiveNetworkId: AptosWalletProvider['getActiveNetworkId'] =
    async () => {
      const aptosNetwork = await features['aptos:network']?.network();

      if (!aptosNetwork?.chainId) {
        return getActiveNetworkIdFromLastKnownRegistry({
          client: dynamicClient,
          walletProviderKey: key,
        });
      }

      return { networkId: aptosNetwork.chainId.toString() };
    };

  const getAptosClient: AptosWalletProvider['getAptosClient'] = async ({
    walletAccount,
  }) => {
    const activeNetworkData = await getActiveNetworkData(
      { walletAccount },
      dynamicClient
    );

    assertDefined(
      activeNetworkData.networkData,
      'No active network data found'
    );

    const { rpcUrls, cluster } = activeNetworkData.networkData;

    const url = rpcUrls.http[0];

    assertDefined(url, 'No RPC URL found for active network');

    const network = cluster as Network;
    const config = new AptosConfig({ fullnode: url, network });

    return new Aptos(config);
  };

  const getConnectedAccount = async (): Promise<AccountInfo | undefined> => {
    const getAccountMethod = features['aptos:account']?.account;

    if (!getAccountMethod) {
      return undefined;
    }

    return getAccountMethod();
  };

  const getConnectedAddresses: AptosWalletProvider['getConnectedAddresses'] =
    async () => {
      const account = await getConnectedAccount();

      if (account) {
        return {
          addresses: [account.address.toString()],
        };
      }

      return {
        addresses: [],
      };
    };

  const signTransaction: AptosWalletProvider['signTransaction'] = async ({
    asFeePayer,
    transaction,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signTransactionMethod =
      features['aptos:signTransaction']?.signTransaction;

    assertDefined(
      signTransactionMethod,
      'signTransaction method not found in wallet features'
    );

    const result = await signTransactionMethod(transaction, asFeePayer);

    if (result.status !== UserResponseStatus.APPROVED) {
      throw new UserRejectedError({ action: 'signTransaction' });
    }

    return {
      signature: result.args,
    };
  };

  const signAndSubmitTransaction: AptosWalletProvider['signAndSubmitTransaction'] =
    async ({ transaction, walletAccount }) => {
      await assertWalletAccountSigningAvailability(
        { walletAccount },
        dynamicClient
      );

      const signAndSubmitMethod =
        features['aptos:signAndSubmitTransaction']?.signAndSubmitTransaction;

      assertDefined(
        signAndSubmitMethod,
        'signAndSubmitTransaction method not found in wallet features'
      );

      const result = await signAndSubmitMethod({
        payload: transaction,
      });

      if (result.status !== UserResponseStatus.APPROVED) {
        throw new UserRejectedError({ action: 'signAndSubmitTransaction' });
      }

      return {
        hash: result.args.hash,
      };
    };

  const signMessage: AptosWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signMessageMethod = features['aptos:signMessage']?.signMessage;

    assertDefined(
      signMessageMethod,
      'signMessage method not found in wallet features'
    );

    const nonce = Date.now().toString();
    const result = await signMessageMethod({
      message,
      nonce,
    });

    if (result.status !== UserResponseStatus.APPROVED) {
      throw new UserRejectedError({ action: 'signMessage' });
    }

    let publicKey: string | undefined =
      walletStandardWallet.accounts[0]?.publicKey?.toString();

    if (!publicKey) {
      const account = await getConnectedAccount();
      publicKey = account?.publicKey?.toString();
    }

    const signatureObj = {
      fullMessage: result.args.fullMessage,
      publicKey,
      signature: result.args.signature.toString(),
    };

    return { signature: JSON.stringify(signatureObj) };
  };

  const { cleanupEventEmitter, getEventEmitter } =
    createWalletProviderEventEmitter<VoidFunction>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleNetworkChanged,
      }): VoidFunction => {
        const onAccountChange =
          features['aptos:onAccountChange']?.onAccountChange;

        if (onAccountChange) {
          void onAccountChange((account) => {
            handleAccountsChanged({
              addresses: account ? [account.address.toString()] : [],
            });
          });
        }

        const onNetworkChange =
          features['aptos:onNetworkChange']?.onNetworkChange;

        if (onNetworkChange) {
          void onNetworkChange((network) => {
            handleNetworkChanged({ networkId: network.chainId.toString() });
          });
        }

        return () => {};
      },

      supportedEvents: ['accountsChanged', 'networkChanged'],
    });

  const terminate: AptosWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  const walletProvider: AptosWalletProvider = {
    chain: 'APTOS',
    connect,
    disconnect,
    get events() {
      return getEventEmitter();
    },
    getActiveNetworkId,
    getAptosClient,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(walletStandardWallet.name),
    key,
    metadata: {
      displayName: walletStandardWallet.name,
      icon: walletStandardWallet.icon,
    },
    signAndSubmitTransaction,
    signMessage,
    signTransaction,
    terminate,
    transferAmount: (args) => aptosTransferAmount(args, dynamicClient),
    walletProviderType,
  };

  return walletProvider;
};
